// Thin fetch wrapper for talking to Rails: adds CSRF + XHR headers, JSON-encodes
// object bodies, and renders Turbo Stream responses automatically.
import { renderStreamMessage } from '@hotwired/turbo'

const ACCEPT = {
  'html': 'text/html, application/xhtml+xml',
  'turbo-stream': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
  'json': 'application/json, application/vnd.api+json',
}

export class FetchResponse {
  constructor(response) {
    this.response = response
  }

  get ok() { return this.response.ok }
  get statusCode() { return this.response.status }
  get headers() { return this.response.headers }

  get contentType() {
    return (this.headers.get('Content-Type') || '').replace(/;.*$/, '')
  }

  get isTurboStream() {
    return this.contentType.startsWith('text/vnd.turbo-stream.html')
  }

  get text() {
    return (this._text ||= this.response.text())
  }

  // A getter returning a promise, so callers write `await response.json`.
  get json() {
    if (!/^application\/.*json$/.test(this.contentType)) {
      return Promise.reject(new Error(`Expected a JSON response but got "${this.contentType}" instead`))
    }
    return (this._json ||= this.response.json())
  }
}

export async function request(method, url, { body, responseKind = 'html', contentType } = {}) {
  const type = contentType ?? (body == null || body instanceof FormData ? undefined : 'application/json')
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': ACCEPT[responseKind] || '*/*',
  }
  if (type) headers['Content-Type'] = type
  if (sameHost(url)) headers['X-CSRF-Token'] = csrfToken()

  const response = new FetchResponse(await fetch(url, {
    method: method.toUpperCase(),
    headers,
    body: type === 'application/json' && typeof body !== 'string' ? JSON.stringify(body) : body,
    credentials: 'same-origin',
  }))

  // 422 is included so validation errors can re-render forms via streams.
  if ((response.ok || response.statusCode === 422) && response.isTurboStream) {
    renderStreamMessage(await response.text)
  }

  return response
}

export const get = (url, options) => request('get', url, options)
export const post = (url, options) => request('post', url, options)
export const put = (url, options) => request('put', url, options)
export const patch = (url, options) => request('patch', url, options)
export const destroy = (url, options) => request('delete', url, options)

function csrfToken() {
  return document.head.querySelector('meta[name="csrf-token"]')?.content
}

function sameHost(url) {
  try {
    return new URL(url, window.location.href).hostname === window.location.hostname
  }
  catch {
    return true
  }
}
